/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { ReactNode } from 'react';

import { Colors, Shadows } from '@zapier/design-system';
import { responsiveStyles } from '@zapier/responsive-utils';
import { isolateComponent } from '@zapier/style-encapsulation';

import { useSize } from '@zapier/upsells/src/hooks';
import { Size } from '@zapier/upsells/src/types';

import { MODAL_STATE } from '../UpgradeModalLayout';

/**
 * This component is used in place of the Zinnia `ModalContent` component. We can't
 * use the default `ModalContent` because it doesn't support changing background
 * color or padding.
 */
const styles = {
  closeButton: css`
    position: absolute;
    right: 40px;
    top: 40px;
    z-index: 1000;
  `,

  root: (modalState: string, size: Size) =>
    responsiveStyles(size, {
      small: css`
        background-color: ${modalState === MODAL_STATE.upgrade
          ? Colors.white // Prevents the checkout flow background from not matching the modal background.
          : Colors.gray5};
        border-radius: 0;
        box-shadow: ${Shadows.elevation5};
        margin: 10px 10px calc(10px + 40px); // 40px handles bottom toolbar in iOS Safari.
        max-height: calc(100vh - 70px);
        overflow: ${modalState === MODAL_STATE.nudge
          ? 'hidden' // Scroll bar messes up some of the nudge images.
          : 'auto'};
        padding: ${modalState === MODAL_STATE.upgrade
          ? 0 // Checkout page has its own padding.
          : '30px'};
        width: 100%;
      `,
      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      medium: css`
        border-radius: 10px;
        margin: 30px;
        max-height: min(calc(100vh - 80px - 60px), 600px);
        max-width: 970px;
        min-width: 550px;
        padding: ${modalState === MODAL_STATE.nudge
          ? 0 // Nudge state has an image that goes all the way to the modal edge
          : 30};
        width: auto;
      `,
    }),
};

type Props = {
  children: ReactNode;
  /** Passed in from the Zinnia `Modal` component. */
  closeButton?: ReactNode;
  modalState: string;
};

export const ModalContent = ({ children, closeButton, modalState }: Props) => {
  const size = useSize();

  return (
    <div css={styles.root(modalState, size)}>
      <div css={styles.closeButton}>{closeButton}</div>
      {children}
    </div>
  );
};
